/**
 * EN-1492: word and line diffs for the keep-or-discard modal. Ported from the design board
 * (OptionA3): LCS over words with case-insensitive keys and whitespace kept on the word, and LCS
 * over list lines. Bold markup is stripped first, as the server's edit comparison does.
 */
export type DiffKind = "same" | "del" | "ins";
export type DiffPart = { text: string; kind: DiffKind };
export type DiffLine = { text: string; kind: DiffKind };

const stripBold = (s: string) => s.replace(/<\/?b>/gi, "");
const words = (s: string) => s.match(/\S+\s*/g) ?? [];

// The table is n x m numbers; past this many cells a long text is diffed by lines first (wordParts), and
// a long edit with no line breaks shows as one replaced block.
const MAX_LCS_CELLS = 250000;

const lcs = (a: string[], b: string[], key: (s: string) => string): Array<[DiffKind, string]> => {
    const n = a.length;
    const m = b.length;
    if (n * m > MAX_LCS_CELLS)
        return [...a.map((t): [DiffKind, string] => ["del", t]), ...b.map((t): [DiffKind, string] => ["ins", t])];
    const table = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            table[i][j] =
                key(a[i]) === key(b[j]) ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
        }
    }
    const out: Array<[DiffKind, string]> = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (key(a[i]) === key(b[j])) {
            out.push(["same", b[j]]);
            i++;
            j++;
        } else if (table[i + 1][j] >= table[i][j + 1]) {
            out.push(["del", a[i++]]);
        } else {
            out.push(["ins", b[j++]]);
        }
    }
    while (i < n) out.push(["del", a[i++]]);
    while (j < m) out.push(["ins", b[j++]]);
    return out;
};

const lowerTrim = (s: string) => s.trim().toLowerCase();

const mergeParts = (pairs: Array<[DiffKind, string]>): DiffPart[] => {
    const merged: DiffPart[] = [];
    for (const [kind, text] of pairs) {
        const last = merged[merged.length - 1];
        if (last && last.kind === kind) last.text += text;
        else merged.push({ text, kind });
    }
    return merged;
};

const wordPairs = (a: string, b: string) => lcs(words(a), words(b), lowerTrim);

// each line keeps its line break, so joining the parts gives back the text
const lines = (s: string) => s.match(/[^\n]*\n|[^\n]+/g) ?? [];

/**
 * Past the word cap (a whole plain-string document, O8), diff by lines first: unchanged lines stay
 * plain, and each run of changed lines is word-diffed on its own (the cap still applies to that run).
 */
const linePairs = (a: string, b: string): Array<[DiffKind, string]> => {
    const out: Array<[DiffKind, string]> = [];
    let del = "";
    let ins = "";
    const flush = () => {
        if (del || ins) out.push(...wordPairs(del, ins));
        del = "";
        ins = "";
    };
    for (const [kind, text] of lcs(lines(a), lines(b), lowerTrim)) {
        if (kind === "same") {
            flush();
            out.push(["same", text]);
        } else if (kind === "del") del += text;
        else ins += text;
    }
    flush();
    return out;
};

/** Word diff with adjacent words of the same kind merged into one part. */
export const wordParts = (a: string, b: string): DiffPart[] => {
    const plainA = stripBold(a);
    const plainB = stripBold(b);
    const overCap = words(plainA).length * words(plainB).length > MAX_LCS_CELLS;
    return mergeParts(overCap ? linePairs(plainA, plainB) : wordPairs(plainA, plainB));
};

export const listLines = (a: string[], b: string[]): DiffLine[] =>
    lcs(a.map(stripBold), b.map(stripBold), lowerTrim).map(([kind, text]) => ({ text, kind }));

const joinText = (v: string | string[] | null | undefined) => (Array.isArray(v) ? v.join("\n") : (v ?? ""));

/** True when the two values differ only in whitespace (a double space, a trailing space), so the diff shows nothing. */
export const differsOnlyInWhitespace = (
    a: string | string[] | null | undefined,
    b: string | string[] | null | undefined,
): boolean => {
    const plainA = stripBold(joinText(a));
    const plainB = stripBold(joinText(b));
    return plainA !== plainB && plainA.replace(/\s+/g, "") === plainB.replace(/\s+/g, "");
};
